// src/components/profile-gate/ProfileGateShell.jsx

import React from "react";

const LOGO_URL =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Icon/3.png";

export default function ProfileGateShell({
  title = "",
  subtitle = "",

  children,

  onBack,
  onNext,

  nextLabel = "NEXT",
  backLabel = "BACK",

  nextDisabled = false,

  showBack = true,
  showFooter = true,
  showHeader = true,

  footerNote = "",

  progress = null,
  progressPercent = null,

  centerContent = false,

  contentClassName = "",
}) {
  const safePercent =
    typeof progressPercent ===
    "number"
      ? Math.max(
          0,
          Math.min(
            100,
            progressPercent
          )
        )
      : null;

  return (
    <div
      className="
        min-h-[100dvh]
        bg-white
        text-black
      "
      style={{
        paddingTop:
          "env(safe-area-inset-top, 0px)",
      }}
    >
      {/* ===================================================
          HEADER
      =================================================== */}

      {showHeader && (
        <header
          className="
            sticky
            top-0
            z-50
            w-full
            border-b
            border-[#EFEFEF]
            bg-white/95
            backdrop-blur
          "
        >
          <div
            className="
              mx-auto
              flex
              w-full
              max-w-5xl
              items-center
              px-3
              py-3

              min-[375px]:px-4

              sm:px-6
              sm:py-4
            "
          >
            <img
              src={LOGO_URL}
              alt="Gwin"
              className="
                h-12
                w-auto
                object-contain

                min-[375px]:h-[52px]

                sm:h-14
                md:h-16
              "
              loading="eager"
              decoding="async"
              draggable="false"
            />
          </div>
        </header>
      )}

      {/* ===================================================
          PAGE CONTENT
      =================================================== */}

      <main
  className={[
    "mx-auto",
    "w-full",
    "max-w-2xl",
    "px-5",
    "pt-6",
    "min-[375px]:px-6",
    "sm:px-8",
    "sm:pt-8",

    showFooter
      ? "pb-[100px]"
      : "pb-10",

    centerContent
      ? "flex min-h-[calc(100dvh-90px)] flex-col justify-center"
      : "",

    contentClassName,
  ].join(" ")}
  style={
    showFooter
      ? {
          paddingBottom:
            "calc(100px + env(safe-area-inset-bottom, 0px))",
        }
      : undefined
  }
>
        {/* ===============================================
            PROGRESS
        =============================================== */}

        {(progress ||
          safePercent !== null) && (
          <div className="mb-7">
            <div
              className="
                mx-auto
                w-full
                max-w-[560px]
              "
            >
              {progress && (
                <div
                  className="
                    mb-3
                    flex
                    items-center
                    justify-between
                    gap-4
                  "
                >
                  <p
                    className="
                      text-[13px]
                      font-bold
                      tracking-tight
                      text-[#333333]

                      sm:text-sm
                    "
                  >
                    {progress}
                  </p>

                  {safePercent !==
                    null && (
                    <p
                      className="
                        text-[12px]
                        font-semibold
                        text-[#9A9A9A]
                      "
                    >
                      {Math.round(
                        safePercent
                      )}
                      %
                    </p>
                  )}
                </div>
              )}

              {safePercent !== null && (
                <div
                  className="
                    h-[7px]
                    w-full
                    overflow-hidden
                    rounded-full
                    bg-[#EEEEEE]
                  "
                >
                  <div
                    className="
                      h-full
                      rounded-full
                      bg-[#FF914D]
                      transition-[width]
                      duration-300
                      ease-out
                    "
                    style={{
                      width: `${safePercent}%`,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===============================================
            OPTIONAL PAGE TITLE
        =============================================== */}

        {(title || subtitle) && (
          <div
            className="
              mx-auto
              mb-7
              w-full
              max-w-[560px]
              text-center
            "
          >
            {title && (
              <h1
                className="
                  text-[clamp(1.6rem,7vw,2.45rem)]
                  font-extrabold
                  leading-[1.12]
                  tracking-[-0.035em]
                  text-black
                "
              >
                {title}
              </h1>
            )}

            {subtitle && (
              <p
                className="
                  mx-auto
                  mt-4
                  max-w-[470px]
                  text-[15px]
                  font-medium
                  leading-relaxed
                  text-[#757575]

                  sm:text-[16px]
                "
              >
                {subtitle}
              </p>
            )}
          </div>
        )}

        {children}

        {!!footerNote && (
          <p
            className="
              mx-auto
              mt-5
              max-w-[520px]
              text-center
              text-xs
              font-medium
              leading-relaxed
              text-[#8A8A8A]

              sm:text-sm
            "
          >
            {footerNote}
          </p>
        )}
      </main>

      {/* ===================================================
          FIXED NAVIGATION
      =================================================== */}

      {showFooter && (
        <footer
          className="
            fixed
            bottom-0
            left-0
            right-0
            z-50
            border-t
            border-[#EFEFEF]
            bg-white/95
            backdrop-blur
          "
          style={{
            paddingBottom:
              "env(safe-area-inset-bottom, 0px)",
          }}
        >
          <div
            className="
              mx-auto
              flex
              w-full
              max-w-2xl
              items-center
              gap-3
              px-5
              py-4

              min-[375px]:px-6

              sm:px-8
            "
          >
            {showBack && (
              <button
                type="button"
                onClick={onBack}
                className="
                  flex
                  min-h-[48px]
                  min-w-[96px]
                  items-center
                  justify-center
                  rounded-2xl
                  bg-[#EFEFEF]
                  px-5
                  text-sm
                  font-extrabold
                  text-[#333333]
                  transition

                  hover:bg-[#E3E3E3]

                  active:scale-[0.98]
                "
              >
                {backLabel}
              </button>
            )}

            <button
              type="button"
              onClick={onNext}
              disabled={nextDisabled}
              className={[
                "flex",
                "min-h-[48px]",
                "flex-1",
                "items-center",
                "justify-center",
                "rounded-2xl",
                "px-5",
                "text-sm",
                "font-extrabold",
                "transition",
                "active:scale-[0.99]",

                nextDisabled
                  ? [
                      "cursor-not-allowed",
                      "bg-[#E2E2E2]",
                      "text-[#9A9A9A]",
                    ].join(" ")
                  : [
                      "bg-[#FF914D]",
                      "text-white",
                      "hover:bg-[#E9782F]",
                    ].join(" "),
              ].join(" ")}
            >
              {nextLabel}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}